import build from './buildVersion.json';
import { reportPwaDiagnostic, diagnosticError } from './pwaDiagnostics';

let started = false;

function showBuildVersion() {
  if (document.getElementById('schoolshield-build-version')) return;
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', showBuildVersion, { once: true });
    return;
  }

  // Use this bundle's version, not the version currently published on the server.
  const version = process.env.NODE_ENV === 'production' ? build.version : 'development';
  let updatedText = 'unavailable';
  try {
    const storageKey = `app-build-loaded:${build.app}:${process.env.NODE_ENV}`;
    let saved = null;
    try { saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null'); }
    catch (_) { /* Replace an unreadable or malformed record if storage allows it. */ }
    const savedTime = saved && Number.isFinite(saved.updatedAt)
      ? new Date(saved.updatedAt).getTime() : NaN;
    let updatedAt = savedTime;
    if (!saved || saved.version !== version || !Number.isFinite(savedTime)) {
      updatedAt = Date.now();
      window.localStorage.setItem(storageKey, JSON.stringify({ version, updatedAt }));
    }
    updatedText = new Date(updatedAt).toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { /* A blocked storage area must not interrupt the app. */ }

  const label = document.createElement('div');
  label.id = 'schoolshield-build-version';
  label.textContent = `Build ${version} | Updated ${updatedText}`;
  label.style.cssText = 'position:fixed;right:8px;right:max(8px, env(safe-area-inset-right));bottom:4px;bottom:max(4px, env(safe-area-inset-bottom));z-index:2147483646;color:#a6a6a6;font:10px/1.2 Arial,sans-serif;white-space:pre;text-align:right;pointer-events:none;user-select:none;';

  const printStyle = document.createElement('style');
  printStyle.textContent = '@media print { #schoolshield-build-version { display: none !important; } }';
  label.appendChild(printStyle);
  document.body.appendChild(label);
}

// Check the deployed release without caching application data or clearing chat IDs.
export function startAppUpdates() {
  showBuildVersion();
  if (started || process.env.NODE_ENV !== 'production' || build.version === 'development') return;
  started = true;
  reportPwaDiagnostic('app_started', {
    controlled: Boolean(navigator.serviceWorker && navigator.serviceWorker.controller),
  });

  const releaseParam = '__ss_release';
  const storageKey = `app-update:${build.app}`;
  const metadataUrl = new URL(`${process.env.PUBLIC_URL || ''}/version.json`, window.location.origin);
  let interacted = false;
  let checking = false;
  let lastCheck = 0;
  let available = null;
  let banner = null;
  let checkHadFailure = false;
  let deferredVersion = null;
  let staleVersion = null;

  const markInteraction = () => { interacted = true; };
  ['pointerdown', 'touchstart', 'keydown', 'input', 'change', 'compositionstart'].forEach(event => {
    document.addEventListener(event, markInteraction, { capture: true, passive: true });
  });

  function hasUnfinishedInput() {
    const focused = document.activeElement;
    if (focused && focused.matches('input, textarea, select, [contenteditable="true"]')) return true;
    return Array.from(document.querySelectorAll('input, textarea, [contenteditable="true"]')).some(field => {
      if (field.isContentEditable) return Boolean(field.textContent.trim());
      if (field.type === 'file') return Boolean(field.files && field.files.length);
      if (['hidden', 'button', 'submit', 'reset', 'checkbox', 'radio'].includes(field.type)) return false;
      return Boolean(field.value);
    });
  }

  function alreadyAttempted(version) {
    if (new URL(window.location.href).searchParams.get(releaseParam) === version) return true;
    try { return window.sessionStorage.getItem(storageKey) === version; }
    catch (_) { return false; }
  }

  function reload(phase = 'automatic') {
    if (!available) return;
    reportPwaDiagnostic('update_reload', { phase, remoteBuild: available });
    try { window.sessionStorage.setItem(storageKey, available); } catch (_) { /* Storage may be disabled. */ }
    const destination = new URL(window.location.href);
    destination.searchParams.set(releaseParam, available);
    // A new URL also bypasses an old HTTP-cached index page. Keep every other parameter.
    if (destination.href === window.location.href) window.location.reload();
    else window.location.replace(destination.href);
  }

  function showUpdate(phase) {
    if (deferredVersion !== available) {
      deferredVersion = available;
      reportPwaDiagnostic('update_deferred', { phase, remoteBuild: available });
    }
    if (banner) return;
    banner = document.createElement('div');
    banner.setAttribute('role', 'status');
    banner.style.cssText = 'position:fixed;bottom:12px;left:50%;transform:translateX(-50%);z-index:2147483647;max-width:calc(100% - 24px);box-sizing:border-box;padding:10px 14px;border-radius:8px;background:#17334a;color:#fff;box-shadow:0 2px 12px #0004;display:flex;align-items:center;gap:12px;font:14px/1.4 sans-serif;';
    const text = document.createElement('span');
    text.textContent = 'Update available. Save unfinished work, then reload.';
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Reload';
    button.style.cssText = 'border:0;border-radius:4px;padding:7px 12px;background:#fff;color:#17334a;font:inherit;font-weight:600;cursor:pointer;';
    button.addEventListener('click', () => reload('user'));
    banner.appendChild(text);
    banner.appendChild(button);
    document.body.appendChild(banner);
  }

  async function check() {
    if (checking || document.visibilityState === 'hidden' || !navigator.onLine || Date.now() - lastCheck < 15000) return;
    checking = true;
    lastCheck = Date.now();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    let phase = 'fetch';
    try {
      const url = new URL(metadataUrl.href);
      url.searchParams.set('_', String(Date.now()));
      const response = await fetch(url.href, { cache: 'no-store', credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) {
        checkHadFailure = true;
        reportPwaDiagnostic('update_check_failed', { phase: 'http', status: response.status });
        return;
      }
      phase = 'metadata';
      const remote = await response.json();
      if (!remote || remote.app !== build.app || !/^[a-f0-9]{24}$/.test(remote.version)) {
        checkHadFailure = true;
        reportPwaDiagnostic('update_check_failed', { phase: 'metadata' });
        return;
      }
      if (checkHadFailure) {
        checkHadFailure = false;
        reportPwaDiagnostic('update_check_recovered', { remoteBuild: remote.version });
      }
      phase = 'apply';
      if (remote.version === build.version) {
        available = null;
        deferredVersion = null;
        staleVersion = null;
        if (banner) { banner.remove(); banner = null; }
        const current = new URL(window.location.href);
        if (current.searchParams.get(releaseParam) === build.version) {
          current.searchParams.delete(releaseParam);
          window.history.replaceState(window.history.state, '', current.href);
          reportPwaDiagnostic('update_applied', { remoteBuild: build.version });
        }
        return;
      }
      if (available !== remote.version) {
        reportPwaDiagnostic('update_available', { remoteBuild: remote.version });
      }
      available = remote.version;
      // Never interrupt typing, a selected file, or an app already used in this tab.
      // One automatic attempt per release prevents loops during partial deployments.
      const inUse = interacted || hasUnfinishedInput();
      const attempted = alreadyAttempted(available);
      if (attempted && staleVersion !== available) {
        staleVersion = available;
        reportPwaDiagnostic('update_still_stale', { remoteBuild: available });
      }
      if (!inUse && !attempted && document.visibilityState !== 'hidden') reload();
      else showUpdate(inUse ? 'in_use' : attempted ? 'already_attempted' : 'hidden');
    } catch (error) {
      checkHadFailure = true;
      reportPwaDiagnostic('update_check_failed', { phase, ...diagnosticError(error) });
      // Offline or a deployment in progress: keep the current screen and retry later.
    } finally {
      window.clearTimeout(timeout);
      checking = false;
    }
  }

  if (document.readyState === 'complete') check();
  else window.addEventListener('load', check, { once: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
  window.addEventListener('online', check);
  window.addEventListener('pageshow', check);
  window.setInterval(check, 5 * 60 * 1000);
}

